import { useState, useEffect } from "react";
import useProfileStore from "../../../store/customer/useProfileStore";
import useAddressStore from "../../../store/useAddressStore";
import { toast } from "react-hot-toast";

const CustomerAddress = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    address_line1: "",
    address_line2: "",
    village: "",
    taluk: "",
    district: "",
    state: "Tamil Nadu",
    postcode: "",
  });

  const {
    profile,
    fetchProfile,
    updateProfile,
    isLoading: isProfileLoading,
    isUpdating,
  } = useProfileStore();
  const {
    districts: storeDistricts,
    taluks: storeTaluks,
    fetchMappedDistricts,
    fetchMappedTaluks,
    isLoadingDistricts,
    isLoadingTaluks,
  } = useAddressStore();

  useEffect(() => {
    if (!profile) {
      fetchProfile();
    }
    fetchMappedDistricts("Tamil Nadu");
  }, [profile, fetchProfile, fetchMappedDistricts]);

  // --- MODIFICATION START ---
  // This effect now populates the form from the profile, but intentionally leaves taluk blank
  // to prevent the race condition. It triggers the fetch for taluks.
  useEffect(() => {
    if (profile) {
      const initialFormData = {
        address_line1: profile.address_line1 || "",
        address_line2: profile.address_line2 || "",
        village: profile.village || "",
        taluk: "", // Intentionally set to empty initially
        district: profile.district || "",
        state: profile.state || "Tamil Nadu",
        postcode: profile.postcode || "",
      };
      setFormData(initialFormData);
      if (initialFormData.district) {
        fetchMappedTaluks(initialFormData.district, "Tamil Nadu");
      }
    }
  }, [profile]); // This effect only depends on `profile` loading

  // This new effect runs AFTER the taluk list is fetched.
  // It then sets the correct taluk value in the form.
  useEffect(() => {
    if (storeTaluks.length > 0 && profile && profile.taluk) {
      if (storeTaluks.includes(profile.taluk)) {
        setFormData((prev) => ({ ...prev, taluk: profile.taluk }));
      }
    }
  }, [storeTaluks, profile]); // It depends on the taluk list and profile data.
  // --- MODIFICATION END ---

  useEffect(() => {
    if (formData.district && formData.district !== profile?.district) {
      fetchMappedTaluks(formData.district, "Tamil Nadu");
    } else if (!formData.district) {
      setFormData((prev) => ({ ...prev, taluk: "" }));
      useAddressStore.setState({ taluks: [] });
    }
  }, [formData.district, profile?.district, fetchMappedTaluks]);


  const handleToggleEdit = () => {
    if (isEditing && profile) {
      const originalFormData = {
        address_line1: profile.address_line1 || "",
        address_line2: profile.address_line2 || "",
        village: profile.village || "",
        taluk: profile.taluk || "",
        district: profile.district || "",
        state: profile.state || "Tamil Nadu",
        postcode: profile.postcode || "",
      };
      setFormData(originalFormData);
      if (originalFormData.district) {
        fetchMappedTaluks(originalFormData.district, "Tamil Nadu");
      }
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (key, value) => {
    let processedValue = value;
    if (key === "postcode") {
      processedValue = value.replace(/\D/g, "").slice(0, 6);
    }

    // --- MODIFICATION START: Capitalize address fields ---
    if (["address_line1", "address_line2", "village"].includes(key) && processedValue.length > 0) {
      processedValue = processedValue.charAt(0).toUpperCase() + processedValue.slice(1);
    }
    // --- MODIFICATION END ---
    const newFormData = { ...formData, [key]: processedValue };
    if (key === "district") {
      newFormData.taluk = "";
    }
    setFormData(newFormData);
  };

  const validateForm = () => {
    if (!formData.address_line1?.trim()) {
      toast.error("Address Line 1 is required.");
      return false;
    }
    if (!formData.district?.trim()) {
      toast.error("District is required.");
      return false;
    }
    if (!formData.taluk?.trim()) {
      toast.error("Taluk is required.");
      return false;
    }
    if (!formData.state?.trim()) {
      toast.error("State is required.");
      return false;
    }
    if (!formData.postcode?.trim()) {
      toast.error("Postcode is required.");
      return false;
    }
    if (
      formData.postcode &&
      (formData.postcode.length !== 6 || !/^\d{6}$/.test(formData.postcode))
    ) {
      toast.error("Postcode must be 6 numeric digits.");
      return false;
    }
    return true;
  };

  // --- MODIFICATION START: Added attemptedSave state ---
  const [attemptedSave, setAttemptedSave] = useState(false);
  // --- MODIFICATION END ---

  const handleSubmit = async () => {
    // --- MODIFICATION START: Set attemptedSave ---
    setAttemptedSave(true);
    // --- MODIFICATION END ---
    if (!validateForm()) return;

    const payload = {
      address_line1: formData.address_line1,
      address_line2: formData.address_line2 || null,
      village: formData.village || null,
      taluk: formData.taluk,
      district: formData.district,
      state: formData.state,
      postcode: formData.postcode || null,
    };

    const success = await updateProfile(payload);
    if (success) {
      setIsEditing(false);
      setAttemptedSave(false);
    }
  };

  const addressFields = [
    { key: "address_line1", label: "Address Line 1", required: true },
    { key: "address_line2", label: "Address Line 2" },
    { key: "village", label: "Village/Town" },
    {
      key: "state",
      label: "State",
      type: "text",
      readOnly: true,
      required: true,
    },
    {
      key: "district",
      label: "District",
      type: "select",
      optionsSource: storeDistricts,
      isLoadingSource: isLoadingDistricts,
      required: true,
    },
    {
      key: "taluk",
      label: "Taluk",
      type: "select",
      optionsSource: storeTaluks,
      isLoadingSource: isLoadingTaluks,
      required: true,
      disabled: !formData.district || isLoadingTaluks,
    },
    {
      key: "postcode",
      label: "Postcode",
      type: "tel",
      required: true,
      maxLength: 6,
    },
  ];

  if (isProfileLoading && !profile) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="w-6 h-6 border-2 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="w-full py-2">
      <div className="text-xs">
        <div className="flex items-center justify-start gap-4 mb-2 border-b border-gray-400 pb-2">
          <h2 className="text-sm font-semibold">Address Details</h2>
          <button
            onClick={handleToggleEdit}
            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs"
            disabled={isUpdating}
          >
            {isEditing ? (isUpdating ? "Saving..." : "Cancel") : "Edit"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-1">
          {addressFields.map(
            ({
              key,
              label,
              required,
              maxLength,
              type,
              optionsSource,
              isLoadingSource,
              readOnly = false,
              disabled = false,
            }) => {
              // Check if field is invalid (empty and required) and user attempted save
              const isInvalid = attemptedSave && required && (!formData[key] || formData[key].toString().trim() === "");

              return (
                <div key={key} className="py-2">
                  <label className="block text-[10px] font-medium text-gray-600">
                    {label}{" "}
                    {required && isEditing && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  {type === "select" ? (
                    <select
                      value={formData[key] || ""}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      disabled={
                        !isEditing || isUpdating || isLoadingSource || disabled
                      }
                      className={`w-full p-2 mt-0.5 rounded-md text-xs ${!isEditing || readOnly
                        ? "border-none bg-gray-50 cursor-default"
                        : `border ${isInvalid ? "border-red-500" : "border-gray-300"} bg-white`
                        } ${isLoadingSource || disabled
                          ? "bg-gray-200 cursor-not-allowed"
                          : ""
                        }`}
                    >
                      <option value="">
                        {isLoadingSource ? "Loading..." : `-- Select ${label} --`}
                      </option>
                      {Array.isArray(optionsSource) &&
                        optionsSource.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <input
                      type={type === "tel" ? "tel" : "text"}
                      inputMode={key === "postcode" ? "numeric" : "text"}
                      value={key === "state" ? "Tamil Nadu" : formData[key] || ""}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      disabled={
                        !isEditing ||
                        isUpdating ||
                        readOnly ||
                        disabled ||
                        key === "state"
                      }
                      maxLength={maxLength}
                      readOnly={readOnly || key === "state"}
                      className={`w-full p-2 mt-0.5 rounded-md text-xs ${!isEditing || readOnly || key === "state"
                        ? "border-none bg-gray-50 cursor-default"
                        : `border ${isInvalid ? "border-red-500" : "border-gray-300"} bg-white`
                        } ${disabled ? "bg-gray-200 cursor-not-allowed" : ""}`}
                    />
                  )}
                </div>
              );
            }
          )}
        </div>
        {isEditing && (
          <div className="flex justify-end mt-2">
            <button
              className={`px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs`}
              onClick={handleSubmit}
              disabled={isUpdating}
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerAddress;